import { DatePicker, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getCounsels } from '../../../api/studentsCounsel.api';
import Button from '../../../components/Button';
import DownloadButton from '../../../components/DownloadButton';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import SelectCampus from '../../../components/SelectCampus';
import SelectCounselType from '../../../components/student/consult/SelectCounselType';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import exportTable from '../../../lib/exportTable';
import { getCommonCodeLabel } from '../../../lib/store';
import {
  ICounselItem,
  IGetCounselList,
} from '../../../models/studentsCounsel.model';
import { RootState } from '../../../store/rootReducer';
import { title } from 'process';

const StudentConsult = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { data: res, isLoading, error, execute } = useAxios<IGetCounselList>();
  const { data: excelRes, isLoading: excelIsLoading, error: excelError, execute: excelExecute } = useAxios<IGetCounselList>();

  const [counselGBN, setCounselGBN] = useState<string>();
  const [writerName, setWriterName] = useState('');
  const [studentName, setStudentName] = useState('');
  const [srtCounselDate, setSrtCounselDate] = useState(moment().subtract(30, 'days').format('YYYYMMDD'));
  const [endCounselDate, setEndCounselDate] = useState(moment().format('YYYYMMDD'));
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});

  useEffect(() => {
    if (excelRes && !excelIsLoading) {
      const _data = _.cloneDeep(excelRes.data);
    _data.forEach((item: any) => {
      item.counselGBN = getCommonCodeLabel('MB0008', item.counselGBN, 'ko');
      item.counselDate = moment(item.counselDate).format('YYYY.MM.DD');
    });
    const filteredColumns: TableColumnsType<ICounselItem> = columns.filter((item: any) => item.dataIndex !== 'campusName');
    // 칼럼 추가
    const ContentColumn = { title: t('student.student.column63'), dataIndex: 'counselContent', key: 'counselContent' };
    // 추가할 column 넣을 index찾기
    const insertContentIndex = filteredColumns.findIndex((item: any) => item.dataIndex === 'writerName');
    // 칼럼 삽입
    filteredColumns.splice(insertContentIndex, 0, ContentColumn);
    
    const currentDate = moment(new Date());
    
    srtCounselDate === String(currentDate.format('YYYYMMDD')) || srtCounselDate === endCounselDate
      ? exportTable(filteredColumns, _data, `${t('student.counseling.title1')}_(${currentDate.format('YYYYMMDD')})`, t('student.counseling.title1'), [10, 12, 12, 48, 52, 10])
      : exportTable(filteredColumns, _data, `${t('student.counseling.title1')}_(${srtCounselDate} ∼ ${endCounselDate})`, t('student.counseling.title1'), [10, 12, 12, 48, 52, 10])
    }
  }, [excelRes, excelIsLoading]);

  useEffect(() => {
    const param: any = {
      pageNo: 1,
      perPage: config.pageSize,
      campusCode: currentCampus,
    };

    if (query.page) {
      param.pageNo = Number(query.page);
      setPage(param.pageNo);
    }
    if (query.counselGBN) {
      param.counselGBN = String(query.counselGBN);
      setCounselGBN(param.counselGBN);
    }
    if (query.writerName) {
      param.writerName = String(query.writerName);
      setWriterName(param.writerName);
    }
    if (query.studentName) {
      param.studentName = String(query.studentName);
      setStudentName(param.studentName);
    }
    if (query.srtCounselDate && query.endCounselDate) {
      param.srtCounselDate = String(query.srtCounselDate);
      param.endCounselDate = String(query.endCounselDate);
      setSrtCounselDate(param.srtCounselDate);
      setEndCounselDate(param.endCounselDate);
    } else if (srtCounselDate && endCounselDate) {
      param.srtCounselDate = srtCounselDate;
      param.endCounselDate = endCounselDate;      
    }

    execute(getCounsels(param));

    param.page = param.pageNo;
    delete param.pageNo;
    delete param.perPage;
    setParam(param);
  }, [location]);

  const onSearch = () => {
    const param: any = { page: 1 };

    if (counselGBN) param.counselGBN = counselGBN;
    if (writerName) param.writerName = writerName;
    if (studentName) param.studentName = studentName;
    if (srtCounselDate && endCounselDate) {
      param.srtCounselDate = srtCounselDate;
      param.endCounselDate = endCounselDate;
    }
    setParam(param);
    history.push(`/student/consult?${QueryString.stringify(param)}`);
  };

  const onExportTable = () => {
    if (param) {
      excelExecute(getCounsels({ ...param, pageNo: 1, perPage: 9999999}));
    }
  }

  const columns: TableColumnsType<ICounselItem> = [
    {
      title: t('student.counseling.column1'),
      dataIndex: 'campusName',
      key: 'campusName',
      align: 'center',
    },
    {
      title: t('student.student.placeholder2'),
      dataIndex: 'studentName',
      key: 'studentName',
      align: 'center',
    },
    {
      title: t('student.student.column34'),
      dataIndex: 'counselGBN',
      key: 'counselGBN',
      align: 'center',
      render: (value: string) => getCommonCodeLabel('MB0008', value, 'ko'),
    },
    {
      title: t('student.counseling.column2'),
      dataIndex: 'counselDate',
      key: 'counselDate',
      align: 'center',
      render: (value: string) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('student.counseling.column3'),
      dataIndex: 'counselTitle',
      key: 'counselTitle',
      align: 'center',
    },
    {
      title: t('student.counseling.column4'),
      dataIndex: 'writerName',
      key: 'writerName',
      align: 'center',
    },
  ];

  return (
    <>
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
                <SelectCampus />
              </div>
          </li>
          <li>
            <label>{t('student.student.column34')}</label>
            <div>
              <SelectCounselType
                counselGBN={counselGBN}
                setCounselGBN={setCounselGBN}
              />
            </div>
          </li>
          <li>
            <label>{t('student.counseling.column4')}</label>
            <div>
              <input
                type="text"
                className="form-control input-text"
                placeholder={t('student.counseling.column4')}
                value={writerName}
                onChange={(e) => setWriterName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSearch();
                }}
              />
            </div>
          </li>
          <li>
            <label>{t('student.student.placeholder2')}</label>
            <div>
              <input
                type="text"
                className="form-control input-text"
                placeholder={t('student.student.placeholder2')}
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSearch();
                }}
              />
            </div>
          </li>
          <li>
            <label>{t('common.excel.period')}</label>
            <div className="date-picker__area">
              <DatePicker
                value={
                  srtCounselDate ? moment(srtCounselDate) : undefined
                }
                onChange={(e) => {
                  if (e === null) setSrtCounselDate('');
                  else setSrtCounselDate(moment(e).format('YYYYMMDD'));
                }}
              />
              ~
              <DatePicker
                value={endCounselDate ? moment(endCounselDate) : undefined}
                onChange={(e) => {
                  if (e === null) setEndCounselDate('');
                  else setEndCounselDate(moment(e).format('YYYYMMDD'));
                }}
              />
            </div>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>	
    <div className="content scrollbar">
      {isLoading || excelIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={excelError} />
      <SimpleHeader
        iconFile="icon-student-big.svg"
        parentMenu={t('common.menu.student')}
        currentMenu={t('student.counseling.title1')}
        onlyTitle={true}
      />

      <div className="col-frame">
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(`/student/consult/${record.counselCode}?${QueryString.stringify(param)}`);
                },
              };
            }}
            rowClassName="pointer"
            rowKey="counselCode"
          />
        </div>
        <div className="comm-bx__between">
          <DownloadButton
            onClick={onExportTable}
          />
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param}
              path="/student/consult"
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default StudentConsult;
